import { useCallback, useLayoutEffect, useState } from 'react'

function checkOverflow(element: HTMLElement): boolean {
  return element.scrollHeight > element.clientHeight
}

/**
 * Allows monitoring whether an element is overflowing its container (with the presence of a scrollbar on desktop)
 * Use the returned `ref` on the element to measure (ref identity stays stable so the effect runs when the node is attached).
 * @returns `{ isOverflowing, ref }` — attach `ref` to the element you want to observe
 */
export function useIsOverflowing(): {
  isOverflowing: boolean
  ref: (instance: HTMLElement | null) => void
} {
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [element, setElement] = useState<HTMLElement | null>(null)

  /**
   * Avoid getting the first ref as null when hook run before DOM element is mounted,
   * making the behaviour unconsistent.
   */
  const ref = useCallback((instance: HTMLElement | null) => {
    setElement(instance)
  }, [])

  useLayoutEffect(() => {
    if (!element) return

    const updateOverflow = () => {
      setIsOverflowing(checkOverflow(element))
    }

    // initial check
    updateOverflow()

    const resizeObserver = new ResizeObserver(updateOverflow)
    resizeObserver.observe(element)
    return () => {
      resizeObserver.disconnect()
    }
  }, [element])

  return { isOverflowing, ref }
}
